import { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Avatar, Box, Container, Skeleton } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import { dateToString } from 'common/utils';
import useGlobalStyles from 'styles/globalStyles';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import { tableColumnProps } from 'common/constants';
import { useGetStores } from 'hooks/useStore';
import StoreActionButtons from '../ActionButtons/StoreActionButtons';

// A time column as the clock reads it, without the seconds.
const toClock = (time?: string) => (time ? time.slice(0, 5) : '-');

export default function StoresTable() {
  const globalStyles = useGlobalStyles();
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [rowCount, setRowCount] = useState(0);

  const { data, isLoading, isRefetching } = useGetStores(
    { page: page + 1, size },
    { refetchOnWindowFocus: false },
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Name" />
      ),
      renderCell: (params) => {
        return (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Avatar
              src={params.row.avatar?.small ?? undefined}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '5px',
                bgcolor: colors.avatarBg,
              }}
            >
              <StorefrontIcon sx={{ fontSize: 18, color: colors.primary }} />
            </Avatar>
            <CustomText
              text={params.row.info?.name ?? '-'}
              fontSize={15}
              fontWeight={500}
              color={colors.black}
            />
          </Box>
        )
      }
    },
    {
      field: 'owner',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Owner" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={params.row.owner?.fullName ?? '-'} />
          </Box>
        )
      }
    },
    {
      field: 'city',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="City" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={params.row.address?.city?.name ?? '-'} />
          </Box>
        )
      }
    },
    {
      field: 'hours',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Hours" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`${toClock(params.row.openingTime)} - ${toClock(params.row.closingTime)}`} />
          </Box>
        )
      }
    },
    {
      field: 'productCount',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Products" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={String(params.row.productCount ?? 0)} />
          </Box>
        )
      }
    },
    {
      field: 'createdAt',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Date" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={dateToString(new Date(params.row.createdAt.split('T')[0]))} />
          </Box>
        )
      }
    },
    {
      field: 'actions',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Actions" />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <StoreActionButtons id={params.row.id} />
          </Box>
        )
      }
    },
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);

  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              backgroundColor: colors.topBackground,
              display: 'inline-flex',
              height: 40,
              width: 40,
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '8px',
            }}
          >
            <StorefrontIcon sx={{ fontSize: 24, color: colors.primary }} />
          </Box>
          <CustomText text="All Stores" fontSize={16} color={colors.primary} fontWeight={600} />
        </Box>

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          disableRowSelectionOnClick
          rows={data?.data || []}
          columns={columns}
          loading={isLoading || isRefetching}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          className={globalStyles.dataGrid}
        />
      </Paper>

      <Box mt={3} />
    </Container>
  );
}
